import { downloadFile, openPdfInNewTab } from "@shared/utils/utils";
import { useRemoteUtilityId, useTenantId } from "../selectors/globalSelectors";
import { getTerritoryConfigMap, getUtilityServicesOptions, sharedApi, getConfigMapJson } from "./api";
import { useSmartMutation, useSmartQuery } from "./queries/hooks";
import { QueryKeyFactory } from "./queries/queryKeyFactory";

export const useGetDashboardData = (dashboardParameter: string, queryParams?: any) => {
  const utilityId = useRemoteUtilityId();
  const tenantId = useTenantId();

  return useSmartQuery(
    QueryKeyFactory.global.dashboard.dashboardData(
      tenantId,
      dashboardParameter,
      utilityId,
      queryParams
    ),
    () => sharedApi.getDashboardData(tenantId, dashboardParameter, utilityId, queryParams),
    {
      enabled: !!tenantId && !!dashboardParameter && !!utilityId,
    },
  );
};

export const useDownloadBill = () => {
  return useSmartMutation(
    async ({
      billId,
      filename = "template.pdf",
    }: {
      billId: string;
      filename?: string;
    }) => {
      const blob = await sharedApi.downloadBillTemplate(billId);
      downloadFile(blob, filename);
      return blob;
    },
  );
};

export const useViewBill = () => {
  return useSmartMutation(async (billId: string) => {
    const blob = await sharedApi.downloadBillTemplate(billId);
    openPdfInNewTab(blob);
    return blob;
  });
};

export const useAreaList = (divisionCodes?: string) => {
  return useSmartQuery(
    QueryKeyFactory.global.onboarding.configMapChoices('area', divisionCodes ),
    () => getTerritoryConfigMap("area", divisionCodes)
  );
};
export const useSubAreaList = (area: string | string[]) => {
  return useSmartQuery(
    QueryKeyFactory.global.onboarding.configMapChoices('subArea', area),
    () => getTerritoryConfigMap("sub_area", area),
  );
};

export const useZoneList = () => {
  return useSmartQuery(
    QueryKeyFactory.global.onboarding.configMapChoices('zone'),
    () => getTerritoryConfigMap("zone"),
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );
};

export const useDivisionList = (zoneCodes: string) => {
  return useSmartQuery(
    QueryKeyFactory.global.onboarding.configMapChoices('division', zoneCodes),
    () => getTerritoryConfigMap("division", zoneCodes),
  );
};

export const useUtilityServicesOptions = (isActive?: boolean) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.global.onboarding.configMapChoices('utility-service', remoteUtilityId),
    () => getUtilityServicesOptions(remoteUtilityId),
    { 
      enabled: !!remoteUtilityId,
      select: (data: any[]) => isActive ? data?.filter((item) => item.isActive === true) : data
    }
  );
};

export const useConfigMapJson = (
  jsonType: "category_json" | "territory_json",
  remoteUtilityId: string,
  configLevel: string,
  codeLevel?: string,
) => {
  return useSmartQuery(
    QueryKeyFactory.module.mx.configMapJson(jsonType, configLevel, codeLevel),
    () =>
      getConfigMapJson({
        jsonType,
        remoteUtilityId,
        configLevel,
        codeLevel,
      }),
    {
      enabled: !!jsonType && !!remoteUtilityId && !!configLevel,
    },
  );
};